
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';
import Logging from '@/logging';
import { parseTime } from '@/utils';
import gql from 'graphql-tag';
import { paymentPlanCancel } from '@/api/payment-plan';
import RsltAgGrid from '@/components/RsltAgGrid/index.vue';

const GET_PAYMENT_PLAN_LIST = gql`
	query ViewPaymentPlan_paymentPlanList(
		$participant: uuid!
		$legalEntity: uuid
		$dateFrom: timestamptz
		$dateTo: timestamptz
	) {
		paymentPlans: risolto_fe_payment_plan_list(
			args: {
				p_participant: $participant
				p_legal_entity: $legalEntity
				p_date_from: $dateFrom
				p_date_to: $dateTo
			}
		) {
			data
			participant
		}
	}
`;

export default defineComponent({
	name: 'PaymentPlanIndex',
	components: {
		RsltAgGrid
	},
	data() {
		return {
			loading: false,
			cancelLoading: false,
			showClosed: false,
			dateFilter: 'past3Months',
			customDateRange: null,
			// Track the currently applied filter state
			appliedFilters: {
				showClosed: false,
				dateFilter: 'past3Months',
				customDateRange: null
			},
			paymentPlans: [] as any[],
			total: -1,
			page: 1,
			limit: 25,
			offset: 0,
			gridApi: null as any,
			columnDefs: [] as any[]
		};
	},
	computed: {
		...mapGetters(['selectedParticipantStore', 'selectedLegalEntityStore']),
		currentFilters() {
			return {
				showClosed: this.showClosed,
				dateFilter: this.dateFilter,
				customDateRange: this.customDateRange
			};
		},
		defaultFilters() {
			return {
				showClosed: false,
				dateFilter: 'past3Months',
				customDateRange: null
			};
		},
		filtersChanged() {
			return JSON.stringify(this.currentFilters) !== JSON.stringify(this.appliedFilters);
		},
		canReset() {
			return JSON.stringify(this.currentFilters) !== JSON.stringify(this.defaultFilters);
		}
	},
	created() {
		this.initializeColumnDefs();
		this.fetchPaymentPlans();
	},
	methods: {
		initializeColumnDefs() {
			this.columnDefs = [
				{
					headerName: this.$t('name'),
					field: 'name',
					width: 200,
					sortable: true,
					filter: 'agTextColumnFilter',
					cellRenderer: (params: any) => {
						if (!params.value) return '';

						const link = document.createElement('a');
						link.href = '#';
						link.className = 'hover-underline fc-blue';
						link.innerText = params.value;
						link.addEventListener('click', (e) => {
							e.preventDefault();
							const path = this.$router.resolve({
								name: 'recipient-detail',
								params: {
									id: params.data.recipient,
									participant: this.selectedParticipantStore
								}
							});
							window.open(path.href, '_blank');
						});
						return link;
					}
				},
				{
					headerName: this.$t('payment_plan.table.total_amount'),
					field: 'total_amount',
					width: 140,
					sortable: true,
					filter: 'agNumberColumnFilter',
					type: 'rightAligned',
					valueFormatter: (params: any) => this.$formatter.toCurrency(params.value, params.data.currency)
				},
				{
					headerName: this.$t('payment_plan.table.open_amount'),
					field: 'open_amount',
					width: 140,
					sortable: true,
					filter: 'agNumberColumnFilter',
					type: 'rightAligned',
					valueFormatter: (params: any) => this.$formatter.toCurrency(params.value, params.data.currency)
				},
				{
					headerName: this.$t('payment_plan.table.overdue_amount'),
					field: 'overdue_amount',
					width: 140,
					sortable: true,
					filter: 'agNumberColumnFilter',
					type: 'rightAligned',
					valueFormatter: (params: any) => params.value ? this.$formatter.toCurrency(params.value, params.data.currency) : '-'
				},
				{
					headerName: this.$t('payment_plan.table.installment_amount'),
					field: 'installment_amount',
					width: 140,
					sortable: true,
					filter: 'agNumberColumnFilter',
					type: 'rightAligned',
					valueFormatter: (params: any) => this.$formatter.toCurrency(params.value, params.data.currency)
				},
				{
					headerName: this.$t('payment_plan.table.installments'),
					field: 'installments',
					width: 120,
					sortable: true,
					filter: 'agNumberColumnFilter',
					cellStyle: { textAlign: 'center' }
				},
				{
					headerName: this.$t('payment_plan.table.installments_paid'),
					field: 'installments_paid',
					width: 120,
					sortable: true,
					filter: 'agNumberColumnFilter',
					cellStyle: { textAlign: 'center' }
				},
				{
					headerName: this.$t('payment_plan.table.installments_overdue'),
					field: 'installments_overdue',
					width: 120,
					sortable: true,
					filter: 'agNumberColumnFilter',
					cellStyle: { textAlign: 'center' },
					valueFormatter: (params: any) => params.value || '0'
				},
				{
					headerName: this.$t('payment_plan.table.on_track'),
					field: 'on_track',
					width: 100,
					sortable: true,
					filter: 'agSetColumnFilter',
					filterParams: {
						valueFormatter: (params: any) => {
							if (params.value === true) return this.$t('yes');
							if (params.value === false) return this.$t('no');
							return '-';
						}
					},
					cellStyle: { textAlign: 'center' },
					cellRenderer: (params: any) => {
						if (params.value === true) {
							return `<span class="el-tag el-tag--success el-tag--mini">${this.$t('yes')}</span>`;
						} else if (params.value === false) {
							return `<span class="el-tag el-tag--danger el-tag--mini">${this.$t('no')}</span>`;
						}
						return '-';
					}
				},
				{
					headerName: this.$t('payment_plan.table.created_at'),
					field: 'created_at',
					width: 140,
					sortable: true,
					filter: 'agDateColumnFilter',
					valueFormatter: (params: any) => this.formatDate(params.value)
				},
				{
					headerName: this.$t('payment_plan.table.first_promise_due_date'),
					field: 'first_promise_due_date',
					width: 140,
					sortable: true,
					filter: 'agDateColumnFilter',
					valueFormatter: (params: any) => this.formatDate(params.value)
				},
				{
					headerName: this.$t('payment_plan.table.next_promise_due_date'),
					field: 'next_promise_due_date',
					width: 140,
					sortable: true,
					filter: 'agDateColumnFilter',
					valueFormatter: (params: any) => params.value ? this.formatDate(params.value) : '-'
				},
				{
					headerName: this.$t('payment_plan.table.missed_promise_due_date'),
					field: 'missed_promise_due_date',
					width: 160,
					sortable: true,
					filter: 'agDateColumnFilter',
					valueFormatter: (params: any) => params.value ? this.formatDate(params.value) : '-'
				},
				{
					headerName: this.$t('payment_plan.table.last_promise_due_date'),
					field: 'last_promise_due_date',
					width: 140,
					sortable: true,
					filter: 'agDateColumnFilter',
					valueFormatter: (params: any) => params.value ? this.formatDate(params.value) : '-'
				},
				{
					headerName: this.$t('payment_plan.table.status'),
					field: 'status',
					width: 200,
					sortable: true,
					filter: 'agSetColumnFilter',
					filterParams: {
						valueFormatter: (params: any) =>
							params.value ? this.$translate.payment_plan_status(params.value) : '-'
					},
					cellRenderer: (params: any) => {
						if (!params.value) return '';
						const tagType = this.getStatusTagType(params.value);
						const statusText = this.$translate.payment_plan_status(params.value);
						return `<span class="el-tag el-tag--${tagType} el-tag--mini">${statusText}</span>`;
					}
				},
				{
					headerName: this.$t('payment_plan.table.approved'),
					field: 'approved_at',
					width: 140,
					sortable: true,
					filter: 'agDateColumnFilter',
					valueFormatter: (params: any) => this.formatDate(params.value)
				},
				{
					headerName: this.$t('payment_plan.table.approved_by'),
					field: 'approved_by_username',
					width: 160,
					sortable: true,
					filter: 'agSetColumnFilter',
					filterParams: {
						valueFormatter: (params: any) => params.value || '-'
					},
					valueFormatter: (params: any) => params.value || '-'
				},
				{
					headerName: this.$t('payment_plan.table.closed'),
					field: 'closed_at',
					width: 140,
					sortable: true,
					filter: 'agDateColumnFilter',
					valueFormatter: (params: any) => this.formatDate(params.data.closed_at || params.data.canceled_at)
				},
				{
					headerName: this.$t('payment_plan.table.closed_by'),
					field: 'canceled_by_username',
					width: 160,
					sortable: true,
					filter: 'agSetColumnFilter',
					filterParams: {
						valueFormatter: (params: any) => params.value || '-'
					},
					valueFormatter: (params: any) => params.value || '-'
				},
				{
					headerName: this.$t('payment_plan.table.interest'),
					field: 'interest',
					width: 120,
					sortable: true,
					filter: 'agNumberColumnFilter',
					type: 'rightAligned',
					valueFormatter: (params: any) => this.$formatter.toCurrency(params.value, params.data.currency)
				},
				{
					headerName: this.$t('payment_plan.table.indemnification'),
					field: 'indemnification',
					width: 140,
					sortable: true,
					filter: 'agNumberColumnFilter',
					type: 'rightAligned',
					valueFormatter: (params: any) => this.$formatter.toCurrency(params.value, params.data.currency)
				},
				{
					headerName: this.$t('payment_plan.table.actions'),
					field: 'actions',
					width: 80,
					pinned: 'right',
					cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'flex-start' },
					cellRenderer: (params: any) => {
						const container = document.createElement('div');
						container.style.display = 'flex';
						container.style.gap = '3px';
						container.style.alignItems = 'center';

						const editBtn = document.createElement('button');
						editBtn.className = 'el-button el-button--primary el-button--mini is-circle';
						editBtn.innerHTML = '<i class="el-icon-edit"></i>';
						editBtn.addEventListener('click', (e) => {
							e.stopPropagation();
							this.handleEdit(params.data);
						});
						container.appendChild(editBtn);

						if (params.data.status === 'OPEN') {
							const cancelBtn = document.createElement('button');
							cancelBtn.className = 'el-button el-button--danger el-button--mini is-circle';
							cancelBtn.innerHTML = '<i class="el-icon-close"></i>';
							cancelBtn.addEventListener('click', (e) => {
								e.stopPropagation();
								this.handleCancel(params.data);
							});
							container.appendChild(cancelBtn);
						}

						return container;
					}
				}
			];
		},
		async fetchPaymentPlans() {
			try {
				this.loading = true;

				// Build query variables
				const variables: any = {
					participant: this.selectedParticipantStore,
					legalEntity: this.selectedLegalEntityStore || null
				};

				// Only apply date filters when showClosed is true
				if (this.showClosed) {
					const dateRange = this.getDateFilterRange();
					if (dateRange) {
						variables.dateFrom = dateRange.start;
						variables.dateTo = dateRange.end;
					}
				} else {
					// When showClosed is false, pass null to get only open plans
					variables.dateFrom = null;
					variables.dateTo = null;
				}

				const result = await this.$apollo.query({
					query: GET_PAYMENT_PLAN_LIST,
					variables
				});

				if (result.data && result.data.paymentPlans) {
					this.paymentPlans = result.data.paymentPlans.map((item: any) => item.data);
					this.total = this.paymentPlans.length;
				}
			} catch (e) {
				Logging.error('Error fetching payment plans:', e);
				this.$message.error(this.$t('error.fetchFailed'));
			} finally {
				this.loading = false;
			}
		},
		getDateFilterRange() {
			if (this.dateFilter === 'all') {
				return null;
			}

			const now = new Date();
			let start: Date;
			let end = new Date(now);

			if (this.dateFilter === 'custom' && this.customDateRange && this.customDateRange.length === 2) {
				start = new Date(this.customDateRange[0]);
				end = new Date(this.customDateRange[1]);
			} else if (this.dateFilter === 'past3Months') {
				start = new Date(now);
				start.setMonth(start.getMonth() - 3);
			} else if (this.dateFilter === 'past6Months') {
				start = new Date(now);
				start.setMonth(start.getMonth() - 6);
			} else if (this.dateFilter === 'past12Months') {
				start = new Date(now);
				start.setMonth(start.getMonth() - 12);
			} else if (this.dateFilter === 'year') {
				start = new Date(now.getFullYear(), 0, 1);
			} else if (this.dateFilter === '2025') {
				start = new Date(2025, 0, 1);
				end = new Date(2025, 11, 31, 23, 59, 59);
			} else if (this.dateFilter === '2024') {
				start = new Date(2024, 0, 1);
				end = new Date(2024, 11, 31, 23, 59, 59);
			} else if (this.dateFilter === '2023') {
				start = new Date(2023, 0, 1);
				end = new Date(2023, 11, 31, 23, 59, 59);
			} else {
				return null;
			}

			return { start: start.toISOString(), end: end.toISOString() };
		},
		getRowId(params: any) {
			return params.data?.payment_plan;
		},
		onGridReady(params: any) {
			this.gridApi = params.api;
		},
		handleApplyFilters() {
			this.appliedFilters = {
				showClosed: this.showClosed,
				dateFilter: this.dateFilter,
				customDateRange: this.customDateRange ? [...this.customDateRange] : null
			};

			this.fetchPaymentPlans();
			if (this.gridApi) {
				this.gridApi.paginationGoToFirstPage();
			}
		},
		handleResetFilters() {
			this.showClosed = false;
			this.dateFilter = 'past3Months';
			this.customDateRange = null;

			this.appliedFilters = {
				showClosed: false,
				dateFilter: 'past3Months',
				customDateRange: null
			};

			if (this.gridApi) {
				this.gridApi.setFilterModel(null);
				this.gridApi.paginationGoToFirstPage();
			}

			this.fetchPaymentPlans();
		},
		handleDateFilterChange() {
			if (this.dateFilter !== 'custom') {
				this.customDateRange = null;
			}
		},
		getStatusTagType(status: string) {
			const statusMap: Record<string, string> = {
				OPEN: 'success',
				PAID: 'info',
				CANCELED_BY_USER: 'warning',
				AUTO_CANCELED_BY_DUNNING: 'warning',
				ABORTED: 'danger'
			};
			return statusMap[status] || '';
		},
		handleCancel(row: any) {
			this.$confirm(
				this.$t('payment-plan.detail.are_you_sure'),
				this.$t('warning'),
				{
					confirmButtonText: this.$t('confirm'),
					cancelButtonText: this.$t('cancel'),
					type: 'warning'
				}
			)
				.then(async () => {
					try {
						this.cancelLoading = true;
						await paymentPlanCancel(
							this.selectedParticipantStore,
							row.legal_entity,
							row.recipient,
							row.payment_plan
						);

						this.$message.success(this.$t('payment_plan.canceled'));
						await this.fetchPaymentPlans();
					} catch (error) {
						Logging.error('Error canceling payment plan:', error);
						this.$message.error(this.$t('error.somethingWentWrong'));
					} finally {
						this.cancelLoading = false;
					}
				})
				.catch(() => {
					// User cancelled
				});
		},
		handleEdit(row: any) {
			this.$router.push({
				name: 'edit-payment-plan',
				params: {
					id: row.payment_plan,
					recipient: row.recipient
				}
			});
		},
		formatDate(dateString: string | null | undefined): string {
			if (!dateString) return '-';
			// Handle ISO date strings (e.g., "2025-03-26" or "2025-03-26T00:00:00.000Z")
			const date = new Date(dateString);
			if (isNaN(date.getTime())) return dateString;
			return parseTime(date, '{d}-{m}-{y}') || dateString;
		},
		handleExport(command: string) {
			const fileName = `${this.$t('routerTitles.Worklist')} ${new Date().toISOString().slice(0, 10)}`;
			if (command === 'csv') {
				this.gridApi.exportDataAsCsv({ fileName });
			} else if (command === 'excel') {
				this.gridApi.exportDataAsExcel({ fileName });
			}
		}
	}
});
